import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PaintBrush, RocketLaunch, Check, X, Gear, Play, Sparkle, ArrowsClockwise, UserCircle, Briefcase, ShieldCheck, Robot, Eye, ChatsCircle, Stamp, ClipboardText, ImageSquare, SquaresFour, Archive, ArrowCounterClockwise } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AdPreview from "@/components/AdPreview";
import TeamChat from "@/components/TeamChat";
import { CarouselCards } from "@/components/CarouselCards";
import { api, API, inr, formatApiErrorDetail } from "@/lib/api";

const STAGE_META = {
  designing: { label: "Designer working…", cls: "bg-ai/10 text-ai border-ai/25", busy: true },
  awaiting_design_approval: { label: "Creative — your approval", cls: "bg-warning/10 text-[#b07600] border-warning/30" },
  building_ad: { label: "Ads Manager building…", cls: "bg-ai/10 text-ai border-ai/25", busy: true },
  in_review: { label: "Senior review…", cls: "bg-ai/10 text-ai border-ai/25", busy: true },
  awaiting_final_approval: { label: "Final sign-off — you", cls: "bg-warning/10 text-[#b07600] border-warning/30" },
  ready_to_publish: { label: "Ready to publish — owner", cls: "bg-[#0052FF]/10 text-[#0052FF] border-[#0052FF]/25" },
  publishing: { label: "Publishing…", cls: "bg-ai/10 text-ai border-ai/25", busy: true },
  published: { label: "Published", cls: "bg-success/10 text-[#00953e] border-success/25" },
  rejected: { label: "Rejected", cls: "bg-[#F3F4F6] text-[#8a90a0] border-[#E5E7EB]" },
  failed: { label: "Failed", cls: "bg-red-50 text-red-500 border-red-200" },
};

const ROLE_ICON = {
  designer: { icon: PaintBrush, name: "Riya · Designer", cls: "text-ai" },
  ads_manager: { icon: Briefcase, name: "Arjun · Ads Manager", cls: "text-[#0052FF]" },
  senior_manager: { icon: ShieldCheck, name: "Meera · Senior Manager", cls: "text-[#b07600]" },
  human: { icon: UserCircle, name: "You", cls: "text-[#00953e]" },
  system: { icon: Robot, name: "System", cls: "text-[#8a90a0]" },
};

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

function JobCard({ job, onAction, busy, onPreview, onChat, brandReady, onApplyBrand, forms, onSetForm, onSetAdset, onUpdateCard, isOwner, canArchive, onArchive }) {
  const [reviseOpen, setReviseOpen] = useState(false);
  const [note, setNote] = useState("");
  const [logOpen, setLogOpen] = useState(false);
  const [logoOpen, setLogoOpen] = useState(false);
  const [logoStyle, setLogoStyle] = useState("badge");
  const [logoCorner, setLogoCorner] = useState("br");
  const [adsets, setAdsets] = useState(null);
  const adsetEditable = ["awaiting_final_approval", "ready_to_publish", "failed"].includes(job.stage) && !!job.ad_package;
  useEffect(() => {
    if (adsetEditable && adsets === null) {
      api.get(`/team/adsets?campaign_id=${job.campaign_id}`).then(({ data }) => setAdsets(data)).catch(() => setAdsets([]));
    }
  }, [adsetEditable, adsets, job.campaign_id]);
  const meta = job.archived ? { label: "Archived", cls: "bg-[#F3F4F6] text-[#8a90a0] border-[#E5E7EB]" } : (STAGE_META[job.stage] || STAGE_META.failed);
  const hasImage = !["designing"].includes(job.stage) && job.design && !job.cards?.length;
  const canBrand = brandReady && job.design && ["awaiting_design_approval", "awaiting_final_approval", "failed"].includes(job.stage);

  return (
    <div data-testid={`job-card-${job.id}`} className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
      <div className="flex flex-col md:flex-row">
        {hasImage && (
          <div className="md:w-64 shrink-0 bg-[#F3F4F6]">
            <img src={`${API}/team/jobs/${job.id}/image?v=${encodeURIComponent(job.updated_at || "")}`} alt={job.design?.concept_name} className="w-full h-full max-h-72 object-cover" loading="lazy" />
          </div>
        )}
        <div className="flex-1 p-5 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={`rounded-full font-bold text-[9px] uppercase border ${meta.cls}`}>
              {meta.busy && <span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-current animate-pulse" />}{meta.label}
            </Badge>
            <span className="font-heading text-sm font-extrabold text-[#0A0D14]">{job.campaign_name}</span>
            <span className="text-[10px] font-mono text-[#8a90a0] ml-auto">{job.date}{job.revision_count ? ` · ${job.revision_count} revision(s)` : ""}</span>
          </div>
          {job.angle && <div className="text-[11px] text-ai font-semibold mt-1">Angle: {job.angle}</div>}

          {job.design && (
            <div className="mt-3">
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-ai"><PaintBrush size={12} weight="fill" /> Riya · Creative Designer</div>
              <div className="text-sm font-bold text-[#0A0D14] mt-0.5">{job.design.concept_name}{job.cards?.length ? <span className="ml-2 rounded-full bg-ai/10 text-ai text-[9px] font-bold px-2 py-0.5 align-middle">CAROUSEL · {job.cards.length} CARDS</span> : null}</div>
              <p className="text-xs text-[#525866] leading-relaxed mt-0.5">{job.design.rationale}</p>
            </div>
          )}

          {job.cards?.length > 0 && (
            <CarouselCards job={job} busy={busy} onUpdateCard={onUpdateCard}
              editable={["awaiting_design_approval", "failed"].includes(job.stage)} />
          )}

          {job.ad_package && (
            <div className="mt-3 bg-[#FAFAFB] border border-[#E5E7EB] rounded-lg p-3">
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[#0052FF]"><Briefcase size={12} weight="fill" /> Arjun · Ads Manager</div>
              <div className="text-sm font-extrabold text-[#0A0D14] mt-1">{job.ad_package.headline}</div>
              <p className="text-xs text-[#525866] leading-relaxed mt-1 whitespace-pre-line">{job.ad_package.primary_text}</p>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] text-[#525866]">
                <span>CTA: <b className="text-[#0A0D14]">{(job.ad_package.cta || "").replace(/_/g, " ")}</b></span>
                <span className="inline-flex items-center gap-1.5">Ad set:{" "}
                  {adsetEditable && (adsets || []).length > 0 ? (
                    <Select value={job.ad_package.adset_id}
                      onValueChange={(v) => onSetAdset(job, v)}>
                      <SelectTrigger className="h-8 w-64 text-xs font-semibold bg-white" data-testid={`job-adset-select-${job.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {!adsets.some((a) => a.id === job.ad_package.adset_id) && (
                          <SelectItem value={job.ad_package.adset_id}>{job.ad_package.adset_name}</SelectItem>
                        )}
                        {adsets.map((a) => (
                          <SelectItem key={a.id} value={a.id}>
                            {a.name}{a.status !== "active" ? " · paused" : ""}{a.leads ? ` · ${a.leads} leads` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <b className="text-[#0A0D14]">{job.ad_package.adset_name}</b>
                  )}
                  {job.ad_package.adset_source && <span className="rounded-full bg-[#F3F4F6] px-2 py-0.5 text-[10px] font-bold text-[#525866]" data-testid={`job-adset-source-${job.id}`}>{job.ad_package.adset_source}</span>}
                </span>
                <span>Budget: <b className="text-[#0A0D14]">{job.ad_package.daily_budget ? `${inr(job.ad_package.current_budget)} → ${inr(job.ad_package.daily_budget)}/day` : "campaign-level (unchanged)"}</b></span>
              </div>
              {job.ad_package.budget_reasoning && <p className="text-[11px] text-[#8a90a0] mt-1.5">{job.ad_package.budget_reasoning}{job.ad_package.guardrail_note ? ` · ${job.ad_package.guardrail_note}` : ""}</p>}
              {adsetEditable && (
                <p className="text-[10px] text-[#8a90a0] mt-1.5" data-testid={`job-adset-hint-${job.id}`}>
                  This ad will launch in the ad set above — the list is pulled live from Meta, so even brand-new empty ad sets appear. No ad needed inside it; 5ads creates the ad for you.
                </p>
              )}
              {job.ad_package.lead_form_id && (
                <div className="mt-2.5 pt-2.5 border-t border-[#E5E7EB]" data-testid={`job-form-row-${job.id}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[#0A0D14]">
                      <ClipboardText size={13} weight="duotone" className="text-[#FF6B35]" /> Instant Form:
                    </span>
                    {["awaiting_final_approval", "ready_to_publish", "failed"].includes(job.stage) && (forms || []).length > 0 ? (
                      <Select value={job.ad_package.lead_form_id}
                        onValueChange={(v) => onSetForm(job, v, (forms.find((f) => f.id === v) || {}).name)}>
                        <SelectTrigger className="h-8 w-60 text-xs font-semibold bg-white" data-testid={`job-form-select-${job.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {!forms.some((f) => f.id === job.ad_package.lead_form_id) && (
                            <SelectItem value={job.ad_package.lead_form_id}>{job.ad_package.lead_form_name}</SelectItem>
                          )}
                          {forms.filter((f) => f.status === "ACTIVE").map((f) => (
                            <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <b className="text-[11px] text-[#0A0D14]">{job.ad_package.lead_form_name}</b>
                    )}
                    <span className="rounded-full bg-[#F3F4F6] px-2 py-0.5 text-[10px] font-bold text-[#525866]" data-testid={`job-form-source-${job.id}`}>{job.ad_package.lead_form_source}</span>
                  </div>
                  {job.stage === "awaiting_final_approval" && (
                    <p className="text-[10px] text-[#8a90a0] mt-1.5">Arjun: confirm this Instant Form is the right one — leads from this ad will land in it.</p>
                  )}
                </div>
              )}
            </div>
          )}

          {job.review && (
            <div className="mt-3">
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[#b07600]"><ShieldCheck size={12} weight="fill" /> Meera · Senior Manager — {job.review.verdict === "approve" ? "APPROVED" : "SENT BACK"}</div>
              <p className="text-xs text-[#525866] leading-relaxed mt-0.5">{job.review.notes}</p>
            </div>
          )}

          {job.error && <p className="mt-3 text-xs text-red-500 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid={`job-error-${job.id}`}>{job.error}</p>}
          {job.signed_off_by && job.stage === "ready_to_publish" && (
            <p className="mt-3 text-[11px] font-semibold text-[#0052FF] bg-[#0052FF]/5 border border-[#0052FF]/20 rounded-lg px-3 py-2" data-testid={`signoff-note-${job.id}`}>
              <Stamp size={12} weight="fill" className="inline mr-1 -mt-0.5" /> Signed off by {job.signed_off_by} · {fmtTime(job.signed_off_at)} — awaiting owner publish
            </p>
          )}
          {job.published_ad_id && <div className="mt-3 text-xs font-mono text-[#00953e]" data-testid={`job-published-${job.id}`}>Live ad on Facebook: {job.published_ad_id}</div>}

          <div className="flex flex-wrap items-center gap-2 mt-4">
            {job.archived ? (
              canArchive && (
                <Button size="sm" variant="outline" onClick={() => onArchive(job, true)} disabled={busy} data-testid={`restore-job-${job.id}`}
                  className="rounded-full border-[#E5E7EB] text-[#525866] hover:text-[#0A0D14] font-bold text-xs h-9 px-4">
                  <ArrowCounterClockwise size={14} className="mr-1.5" /> Restore to pipeline
                </Button>
              )
            ) : (
              <>
            {job.stage === "awaiting_design_approval" && !reviseOpen && (
              <>
                <Button size="sm" onClick={() => onAction(job, "approve-design")} disabled={busy} data-testid={`approve-design-${job.id}`}
                  className="rounded-full bg-[#0A0D14] hover:bg-[#1c2230] text-white font-bold text-xs h-9 px-4">
                  <Check size={14} weight="bold" className="mr-1" /> Approve Creative
                </Button>
                <Button size="sm" variant="outline" onClick={() => setReviseOpen(true)} data-testid={`revise-design-${job.id}`}
                  className="rounded-full border-ai/30 text-ai hover:bg-ai/5 hover:text-ai font-bold text-xs h-9 px-4">
                  <ArrowsClockwise size={14} className="mr-1" /> Ask Revision
                </Button>
              </>
            )}
            {reviseOpen && (
              <div className="flex flex-1 gap-2 min-w-[280px]">
                <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="What should Riya change? e.g. 'less text, show a real classroom'"
                  data-testid={`revise-note-${job.id}`} className="h-9 rounded-lg text-xs flex-1" />
                <Button size="sm" onClick={() => { onAction(job, "revise-design", note); setReviseOpen(false); setNote(""); }} disabled={busy}
                  data-testid={`revise-submit-${job.id}`} className="rounded-full bg-ai hover:bg-[#5457e0] text-white font-bold text-xs h-9 px-4">Send</Button>
                <Button size="sm" variant="outline" onClick={() => setReviseOpen(false)} className="rounded-full text-xs h-9 px-3 border-[#E5E7EB]">Cancel</Button>
              </div>
            )}
            {(job.stage === "awaiting_final_approval" || job.stage === "failed") && job.ad_package && (isOwner ? (
              <Button size="sm" onClick={() => onAction(job, "approve-final")} disabled={busy} data-testid={`approve-final-${job.id}`}
                className="rounded-full bg-[#0052FF] hover:bg-[#0040CC] text-white font-bold text-xs h-9 px-4">
                <RocketLaunch size={14} weight="fill" className="mr-1.5" /> {busy ? "Publishing…" : job.stage === "failed" ? "Retry Publish" : "Approve & Publish to Facebook"}
              </Button>
            ) : (
              <Button size="sm" onClick={() => onAction(job, "approve-final")} disabled={busy} data-testid={`signoff-final-${job.id}`}
                className="rounded-full bg-[#0A0D14] hover:bg-[#1c2230] text-white font-bold text-xs h-9 px-4">
                <Stamp size={14} weight="fill" className="mr-1.5" /> {busy ? "Sending…" : "Sign off & send to owner"}
              </Button>
            ))}
            {job.stage === "ready_to_publish" && job.ad_package && (isOwner ? (
              <Button size="sm" onClick={() => onAction(job, "approve-final")} disabled={busy} data-testid={`publish-now-${job.id}`}
                className="rounded-full bg-[#0052FF] hover:bg-[#0040CC] text-white font-bold text-xs h-9 px-4 ring-2 ring-[#0052FF]/30 ring-offset-1">
                <RocketLaunch size={14} weight="fill" className="mr-1.5" /> {busy ? "Publishing…" : "Publish to Facebook"}
              </Button>
            ) : (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#0052FF]/8 text-[#0052FF] text-[11px] font-bold px-3 py-2" data-testid={`awaiting-owner-${job.id}`}>
                <Stamp size={13} weight="fill" /> Signed off — awaiting owner publish
              </span>
            ))}
            {job.stage === "failed" && !job.ad_package && job.design && (
              <Button size="sm" onClick={() => onAction(job, "approve-design")} disabled={busy} data-testid={`retry-build-${job.id}`}
                className="rounded-full bg-[#0A0D14] hover:bg-[#1c2230] text-white font-bold text-xs h-9 px-4">
                <ArrowsClockwise size={14} className="mr-1.5" /> Retry Ad Build
              </Button>
            )}
            {!["published", "rejected"].includes(job.stage) && !reviseOpen && (
              <Button size="sm" variant="outline" onClick={() => onAction(job, "reject")} disabled={busy} data-testid={`reject-job-${job.id}`}
                className="rounded-full border-[#E5E7EB] text-[#525866] hover:bg-red-50 hover:text-red-500 font-bold text-xs h-9 px-3">
                <X size={14} weight="bold" />
              </Button>
            )}
            {job.ad_package && (
              <Button size="sm" variant="outline" onClick={() => onPreview(job)} data-testid={`preview-ad-${job.id}`}
                className="rounded-full border-[#0052FF]/25 text-[#0052FF] hover:bg-[#0052FF]/5 hover:text-[#0052FF] font-bold text-xs h-9 px-4">
                <Eye size={14} className="mr-1.5" /> Preview Ad
              </Button>
            )}
            {job.design && (
              <Button size="sm" variant="outline" onClick={() => onChat(job)} data-testid={`chat-team-${job.id}`}
                className="rounded-full border-ai/30 text-ai hover:bg-ai/5 hover:text-ai font-bold text-xs h-9 px-4">
                <ChatsCircle size={14} weight="duotone" className="mr-1.5" /> Chat with Team
              </Button>
            )}
            {canBrand && !logoOpen && (
              <Button size="sm" variant="outline" onClick={() => setLogoOpen(true)} data-testid={`apply-logo-${job.id}`}
                className="rounded-full border-[#E5E7EB] text-[#525866] hover:text-[#0A0D14] font-bold text-xs h-9 px-4">
                <Stamp size={14} weight="duotone" className="mr-1.5" /> Logo
              </Button>
            )}
            {logoOpen && (
              <div className="flex flex-wrap items-center gap-2" data-testid={`logo-controls-${job.id}`}>
                <Select value={logoStyle} onValueChange={setLogoStyle}>
                  <SelectTrigger className="w-32 rounded-full h-9 text-xs" data-testid={`logo-style-${job.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="badge">White badge</SelectItem>
                    <SelectItem value="direct">Direct</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={logoCorner} onValueChange={setLogoCorner}>
                  <SelectTrigger className="w-32 rounded-full h-9 text-xs" data-testid={`logo-corner-${job.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="br">Bottom right</SelectItem>
                    <SelectItem value="bl">Bottom left</SelectItem>
                    <SelectItem value="tr">Top right</SelectItem>
                    <SelectItem value="tl">Top left</SelectItem>
                  </SelectContent>
                </Select>
                <Button size="sm" onClick={() => { onApplyBrand(job, logoStyle, logoCorner); setLogoOpen(false); }} disabled={busy}
                  data-testid={`logo-apply-${job.id}`} className="rounded-full bg-[#0A0D14] hover:bg-[#1c2230] text-white font-bold text-xs h-9 px-4">
                  <Stamp size={14} className="mr-1" /> Stamp Logo
                </Button>
                <Button size="sm" variant="outline" onClick={() => setLogoOpen(false)} className="rounded-full text-xs h-9 px-3 border-[#E5E7EB]">Cancel</Button>
              </div>
            )}
            {canArchive && job.stage !== "published" && !reviseOpen && (
              <Button size="sm" variant="outline" onClick={() => onArchive(job, false)} disabled={busy} title="Archive — remove from the pipeline without deleting"
                data-testid={`archive-job-${job.id}`}
                className="rounded-full border-[#E5E7EB] text-[#525866] hover:bg-[#F3F4F6] hover:text-[#0A0D14] font-bold text-xs h-9 px-3">
                <Archive size={14} weight="duotone" />
              </Button>
            )}
              </>
            )}
            <button onClick={() => setLogOpen(!logOpen)} data-testid={`toggle-log-${job.id}`} className="ml-auto text-[11px] font-bold text-[#8a90a0] hover:text-[#0A0D14]">
              {logOpen ? "Hide" : "Team log"} ({(job.log || []).length})
            </button>
          </div>

          {logOpen && (
            <div className="mt-3 border-t border-[#E5E7EB]/60 pt-3 space-y-2" data-testid={`job-log-${job.id}`}>
              {(job.log || []).map((l, i) => {
                const r = ROLE_ICON[l.role] || ROLE_ICON.system;
                const Icon = r.icon;
                return (
                  <div key={i} className="flex items-start gap-2 text-[11px]">
                    <Icon size={14} weight="duotone" className={`${r.cls} mt-0.5 shrink-0`} />
                    <span className="text-[#525866] leading-snug"><b className="text-[#0A0D14]">{r.name}</b> · {l.note} <span className="text-[#c0c4cf] font-mono">{fmtTime(l.at)}</span></span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Team() {
  const [jobs, setJobs] = useState(null);
  const [plan, setPlan] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [settings, setSettings] = useState(null);
  const [pub, setPub] = useState({ page_id: "", landing_url: "" });
  const [pages, setPages] = useState([]);
  const [showPub, setShowPub] = useState(false);
  const [brand, setBrand] = useState({ has_logo: false, colors: [] });
  const navigate = useNavigate();
  const { user } = useAuth();
  const isOwner = user?.role === "owner" || user?.role === "admin" || !!user?.can_publish;
  const [adhocId, setAdhocId] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [proposing, setProposing] = useState(false);
  const [running, setRunning] = useState(false);
  const [previewJob, setPreviewJob] = useState(null);
  const [chatJob, setChatJob] = useState(null);
  const [leadForms, setLeadForms] = useState([]);
  const [runFormat, setRunFormat] = useState("single");
  const [runCardCount, setRunCardCount] = useState(4);
  const pollRef = useRef(null);

  const loadJobs = useCallback(() => api.get("/team/jobs").then(({ data }) => setJobs(data)).catch(() => {}), []);
  const loadPlan = useCallback(() => api.get("/team/plan").then(({ data }) => setPlan(data)).catch(() => {}), []);

  useEffect(() => {
    loadJobs();
    loadPlan();
    api.get("/team/settings").then(({ data }) => setSettings(data)).catch(() => {});
    api.get("/campaigns").then(({ data }) => setCampaigns(data.filter((c) => c.status === "active"))).catch(() => {});
    api.get("/settings/publishing").then(({ data }) => setPub(data)).catch(() => {});
    api.get("/meta/pages").then(({ data }) => setPages(data)).catch(() => {});
    api.get("/team/brand").then(({ data }) => setBrand(data)).catch(() => {});
    api.get("/forms").then(({ data }) => setLeadForms(data.forms || [])).catch(() => {});
  }, [loadJobs, loadPlan]);

  useEffect(() => {
    const anyBusy = (jobs || []).some((j) => STAGE_META[j.stage]?.busy);
    clearInterval(pollRef.current);
    pollRef.current = setInterval(loadJobs, anyBusy ? 6000 : 30000);
    return () => clearInterval(pollRef.current);
  }, [jobs, loadJobs]);

  const toggleTeam = async (v) => {
    setSettings({ ...settings, enabled: v });
    await api.put("/team/settings", { enabled: v }).catch(() => {});
    toast.success(v ? "Creative Team ON — one creative per planned day" : "Creative Team paused");
  };

  const savePub = async () => {
    try {
      const page = pages.find((p) => p.id === pub.page_id);
      const { data } = await api.put("/settings/publishing", { ...pub, page_name: page?.name });
      setPub(data);
      setShowPub(false);
      toast.success("Publishing settings saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const applyBrand = async (job, style, corner) => {
    setBusyId(job.id);
    try {
      await api.post(`/team/jobs/${job.id}/apply-brand`, { style, corner });
      toast.success("Your real logo is stamped on the creative — zero AI involvement");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail), { duration: 8000 });
    } finally {
      setBusyId(null);
      loadJobs();
    }
  };

  const propose = async () => {
    setProposing(true);
    try {
      const { data } = await api.post("/team/plan/propose");
      setPlan(data);
      toast.success("AI proposed this week's creative calendar — edit any day below");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail), { duration: 8000 });
    } finally {
      setProposing(false);
    }
  };

  const [adsetMap, setAdsetMap] = useState({});
  const ensureAdsets = useCallback((cid) => {
    if (!cid) return;
    setAdsetMap((m) => {
      if (m[cid]) return m;
      api.get(`/team/adsets?campaign_id=${cid}`).then(({ data }) => setAdsetMap((mm) => ({ ...mm, [cid]: data }))).catch(() => {});
      return { ...m, [cid]: [] };
    });
  }, []);
  useEffect(() => { (plan?.days || []).forEach((d) => ensureAdsets(d.campaign_id)); }, [plan, ensureAdsets]);

  const setDayCampaign = async (date, campaign_id) => {
    ensureAdsets(campaign_id);
    const days = plan.days.map((d) => (d.date === date ? { ...d, campaign_id, campaign_name: campaigns.find((c) => c.id === campaign_id)?.name, adset_id: null, adset_name: null } : d));
    setPlan({ ...plan, days });
    try {
      const { data } = await api.put("/team/plan", { week_start: plan.week_start, days });
      setPlan(data);
      toast.success("Weekly plan updated");
    } catch (e) {
      toast.error("Plan update failed");
    }
  };

  const setDayAdset = async (date, adset_id) => {
    const day = plan.days.find((d) => d.date === date);
    const auto = adset_id === "auto";
    const name = auto ? null : ((adsetMap[day.campaign_id] || []).find((a) => a.id === adset_id) || {}).name;
    const days = plan.days.map((d) => (d.date === date ? { ...d, adset_id: auto ? null : adset_id, adset_name: name } : d));
    setPlan({ ...plan, days });
    try {
      const { data } = await api.put("/team/plan", { week_start: plan.week_start, days });
      setPlan(data);
      toast.success(auto ? "Ad set on auto — Arjun picks the best performer" : `Locked to '${name}' — the ad will launch there`);
    } catch (e) {
      toast.error("Plan update failed");
    }
  };

  const runToday = async () => {
    setRunning(true);
    try {
      await api.post("/team/run-today", { campaign_id: adhocId || null, format: runFormat, card_count: runCardCount });
      toast.success(runFormat === "carousel"
        ? `Riya is designing a ${runCardCount}-card carousel (~2-3 min) — it will appear below for your approval`
        : "Riya is designing today's creative (~1 min) — it will appear below for your approval");
      setAdhocId("");
      loadJobs();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail), { duration: 8000 });
    } finally {
      setRunning(false);
    }
  };

  const onAction = async (job, verb, note) => {
    setBusyId(job.id);
    try {
      const { data } = await api.post(`/team/jobs/${job.id}/${verb}`, note !== undefined ? { note } : {});
      if (verb === "approve-design") toast.success("Creative approved — Arjun is building the ad, Meera will review it");
      if (verb === "revise-design") toast.success("Sent back to Riya with your note");
      if (verb === "approve-final") {
        if (data.stage === "ready_to_publish") toast.success("Signed off — sent to the owner for publishing", { duration: 8000 });
        else toast.success(`Published ACTIVE to Facebook — ad ${data.published_ad_id} (now in Meta review)`, { duration: 10000 });
      }
      if (verb === "reject") toast.success("Job rejected");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail), { duration: 9000 });
    } finally {
      setBusyId(null);
      loadJobs();
    }
  };

  const setJobForm = async (job, formId, formName) => {
    try {
      await api.put(`/team/jobs/${job.id}/form`, { form_id: formId, form_name: formName });
      toast.success(`Instant Form set to '${formName || formId}' for this ad`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail), { duration: 8000 });
    } finally {
      loadJobs();
    }
  };

  const setJobAdset = async (job, adsetId) => {
    try {
      const { data } = await api.put(`/team/jobs/${job.id}/adset`, { adset_id: adsetId });
      toast.success(`Ad set changed — this ad will launch in '${data.adset_name}'`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail), { duration: 8000 });
    } finally {
      loadJobs();
    }
  };

  const updateCard = async (job, idx, payload) => {
    try {
      const { data } = await api.put(`/team/jobs/${job.id}/card/${idx}`, payload);
      if (data.redo_queued) toast.success(`Riya is redesigning card ${idx + 1}'s image (~30s)`);
      else toast.success(`Card ${idx + 1} updated`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail), { duration: 8000 });
    } finally {
      loadJobs();
    }
  };

  const archiveJob = async (job, restore = false) => {
    setBusyId(job.id);
    try {
      await api.post(`/team/jobs/${job.id}/${restore ? "unarchive" : "archive"}`);
      toast.success(restore ? "Restored to the pipeline" : "Archived — find it anytime under the Archived tab");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail), { duration: 8000 });
    } finally {
      setBusyId(null);
      loadJobs();
    }
  };

  const pubReady = pub.page_id && pub.landing_url;
  const todayIso = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
  const canArchive = user?.role === "owner" || user?.role === "admin";
  const [pipeTab, setPipeTab] = useState("action");
  const NEEDS_ACTION = ["awaiting_design_approval", "awaiting_final_approval", "ready_to_publish", "failed"];
  const IN_PROGRESS = ["designing", "building_ad", "in_review", "publishing"];
  const groups = {
    action: (jobs || []).filter((j) => !j.archived && NEEDS_ACTION.includes(j.stage)),
    progress: (jobs || []).filter((j) => !j.archived && IN_PROGRESS.includes(j.stage)),
    published: (jobs || []).filter((j) => !j.archived && j.stage === "published"),
    archived: (jobs || []).filter((j) => j.archived || j.stage === "rejected"),
  };
  const TAB_EMPTY = {
    action: "Nothing waiting on you — the team is on it.",
    progress: "Nothing in progress right now.",
    published: "No published ads yet.",
    archived: "Nothing archived — use the archive button on any card to tidy the pipeline.",
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="creative-team-page">
      <div className="ai-panel rounded-lg p-6" data-testid="team-header-card">
        <div className="flex flex-wrap items-center gap-4">
          <span className="h-10 w-10 rounded-lg bg-ai/10 flex items-center justify-center"><PaintBrush size={20} weight="duotone" className="text-ai" /></span>
          <div className="mr-auto">
            <h2 className="font-heading text-base font-extrabold tracking-tight text-[#0A0D14]">AI Creative Team</h2>
            <p className="text-[11px] text-[#8a90a0]">Riya designs → you approve → Arjun builds the ad → Meera reviews → you sign off → live on Facebook</p>
          </div>
          {settings && (
            <div className="flex items-center gap-2">
              <Switch checked={settings.enabled} onCheckedChange={toggleTeam} data-testid="team-enabled-switch" />
              <span className={`text-xs font-bold ${settings.enabled ? "text-[#00953e]" : "text-[#8a90a0]"}`}>{settings.enabled ? "ON" : "OFF"}</span>
            </div>
          )}
          <button onClick={() => navigate("/brand")} data-testid="team-brand-toggle"
            className={`inline-flex items-center gap-1.5 text-xs font-bold rounded-full border px-3 py-1.5 transition-colors ${brand.has_logo ? "border-success/30 text-[#00953e] bg-success/5" : "border-warning/30 text-[#b07600] bg-warning/5"}`}>
            <Stamp size={13} /> {brand.has_logo ? "Brand Kit ✓" : "Set up Brand Kit"}
          </button>
          <button onClick={() => setShowPub(!showPub)} data-testid="team-pub-toggle"
            className={`inline-flex items-center gap-1.5 text-xs font-bold rounded-full border px-3 py-1.5 transition-colors ${pubReady ? "border-success/30 text-[#00953e] bg-success/5" : "border-warning/30 text-[#b07600] bg-warning/5"}`}>
            <Gear size={13} /> {pubReady ? `Publishing: ${pub.page_name || pub.page_id}` : "Set up publishing"}
          </button>
        </div>

        {showPub && (
          <div className="flex flex-wrap items-end gap-3 bg-white/70 border border-[#E5E7EB] rounded-lg p-4 mt-4" data-testid="team-pub-settings">
            <div className="space-y-1">
              <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a90a0]">Facebook Page</span>
              <Select value={pub.page_id || ""} onValueChange={(v) => setPub({ ...pub, page_id: v })}>
                <SelectTrigger className="w-56 rounded-lg bg-white h-10" data-testid="team-page-select"><SelectValue placeholder="Select page" /></SelectTrigger>
                <SelectContent>{pages.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
              {pages.length === 0 && (
                <p className="text-[11px] text-[#b07600] font-semibold max-w-[230px] leading-snug" data-testid="team-pages-empty-hint">
                  No Pages found — Facebook didn't share any Page with 5ads.ai. Reconnect on the Connect page and tick your Page on the permission screen ("Edit access").
                </p>
              )}
            </div>
            <div className="space-y-1 flex-1 min-w-[220px]">
              <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a90a0]">Landing URL</span>
              <Input value={pub.landing_url || ""} onChange={(e) => setPub({ ...pub, landing_url: e.target.value })}
                placeholder="https://trainingbasket.in" data-testid="team-landing-input" className="h-10 rounded-lg bg-white" />
            </div>
            <Button onClick={savePub} data-testid="team-pub-save" className="rounded-full bg-[#0A0D14] hover:bg-[#1c2230] text-white font-bold text-xs h-10 px-5">Save</Button>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 mt-4">
          <Select value={adhocId} onValueChange={setAdhocId}>
            <SelectTrigger className="w-72 rounded-lg bg-white h-10" data-testid="team-adhoc-select"><SelectValue placeholder="Today's planned campaign (or pick one)" /></SelectTrigger>
            <SelectContent>{campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <div className="flex items-center rounded-lg border border-[#E5E7EB] bg-white h-10 p-1" data-testid="run-format-picker">
            <button onClick={() => setRunFormat("single")} data-testid="format-single-btn"
              className={`inline-flex items-center gap-1.5 rounded-md px-3 h-8 text-xs font-bold transition-colors ${runFormat === "single" ? "bg-[#0A0D14] text-white" : "text-[#525866] hover:bg-[#F3F4F6]"}`}>
              <ImageSquare size={14} weight="duotone" /> Single
            </button>
            <button onClick={() => setRunFormat("carousel")} data-testid="format-carousel-btn"
              className={`inline-flex items-center gap-1.5 rounded-md px-3 h-8 text-xs font-bold transition-colors ${runFormat === "carousel" ? "bg-[#0A0D14] text-white" : "text-[#525866] hover:bg-[#F3F4F6]"}`}>
              <SquaresFour size={14} weight="duotone" /> Carousel
            </button>
          </div>
          {runFormat === "carousel" && (
            <div className="flex items-center gap-1.5" data-testid="card-count-picker">
              {[3, 4, 5].map((n) => (
                <button key={n} onClick={() => setRunCardCount(n)} data-testid={`card-count-${n}`}
                  className={`h-8 w-8 rounded-full border text-xs font-bold transition-colors ${runCardCount === n ? "border-ai bg-ai text-white" : "border-[#E5E7EB] text-[#525866] bg-white"}`}>{n}</button>
              ))}
              <span className="text-[10px] text-[#8a90a0] font-semibold">cards</span>
            </div>
          )}
          <Button onClick={runToday} disabled={running} data-testid="team-run-button"
            className="rounded-full bg-ai hover:bg-[#5457e0] text-white font-bold h-10 px-5">
            <Play size={15} weight="fill" className="mr-1.5" /> {running ? "Starting…" : "Run Today's Creative"}
          </Button>
        </div>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid="weekly-plan-card">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <h3 className="font-heading text-sm font-extrabold text-[#0A0D14] mr-auto">Weekly Creative Calendar <span className="text-[#8a90a0] font-normal text-xs">week of {plan?.week_start}</span></h3>
          {plan?.proposed_by && <Badge className="rounded-full font-bold text-[9px] uppercase border bg-ai/10 text-ai border-ai/25">{plan.proposed_by === "ai" ? "AI proposed" : "edited by you"}</Badge>}
          <Button size="sm" variant="outline" onClick={propose} disabled={proposing} data-testid="propose-plan-button"
            className="rounded-full border-ai/30 text-ai hover:bg-ai/5 hover:text-ai font-bold text-xs h-9 px-4">
            <Sparkle size={14} weight="fill" className="mr-1.5" /> {proposing ? "AI planning…" : plan?.days?.length ? "Re-propose with AI" : "Propose with AI"}
          </Button>
        </div>
        {!plan?.days?.length ? (
          <p className="text-xs text-[#8a90a0]" data-testid="plan-empty">No plan yet for this week — hit "Propose with AI" and edit any day, or wait for Monday's auto-proposal.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
            {plan.days.map((d) => (
              <div key={d.date} data-testid={`plan-day-${d.date}`}
                className={`rounded-lg border p-3 ${d.date === todayIso ? "border-ai bg-ai/5" : "border-[#E5E7EB] bg-[#FAFAFB]"}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a90a0]">{d.weekday}</span>
                  <span className="text-[10px] font-mono text-[#8a90a0]">{d.date.slice(8)}/{d.date.slice(5, 7)}</span>
                </div>
                <Select value={d.campaign_id || ""} onValueChange={(v) => setDayCampaign(d.date, v)}>
                  <SelectTrigger className="w-full rounded-md bg-white h-8 mt-2 text-[11px]" data-testid={`plan-select-${d.date}`}>
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>{campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
                {d.campaign_id && (
                  <Select value={d.adset_id || "auto"} onValueChange={(v) => setDayAdset(d.date, v)}>
                    <SelectTrigger className={`w-full rounded-md h-8 mt-1.5 text-[11px] ${d.adset_id ? "bg-[#0052FF]/5 border-[#0052FF]/25 text-[#0052FF] font-bold" : "bg-white"}`} data-testid={`plan-adset-select-${d.date}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Ad set: Auto (AI picks)</SelectItem>
                      {(adsetMap[d.campaign_id] || []).map((a) => (
                        <SelectItem key={a.id} value={a.id}>{a.name}{a.status !== "active" ? " · paused" : ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {d.angle && <p className="text-[10px] text-[#8a90a0] leading-snug mt-1.5 line-clamp-2">{d.angle}</p>}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2" data-testid="pipeline-tabs">
          <h3 className="font-heading text-sm font-extrabold text-[#0A0D14] mr-2">Pipeline</h3>
          {[["action", "Needs Action"], ["progress", "In Progress"], ["published", "Published"], ["archived", "Archived"]].map(([k, l]) => (
            <button key={k} onClick={() => setPipeTab(k)} data-testid={`pipeline-tab-${k}`}
              className={`h-8 px-3.5 rounded-full text-[11px] font-bold transition-colors ${pipeTab === k ? "bg-[#0A0D14] text-white" : "bg-white border border-[#E5E7EB] text-[#525866] hover:bg-[#F3F4F6]"}`}>
              {l}{groups[k].length ? ` (${groups[k].length})` : ""}
            </button>
          ))}
        </div>
        {!jobs ? null : jobs.length === 0 ? (
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-10 text-center text-sm text-[#8a90a0]" data-testid="pipeline-empty">
            No creatives yet — hit "Run Today's Creative" or let the daily schedule kick in.
          </div>
        ) : groups[pipeTab].length === 0 ? (
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-8 text-center text-sm text-[#8a90a0]" data-testid={`pipeline-tab-empty-${pipeTab}`}>
            {TAB_EMPTY[pipeTab]}
          </div>
        ) : (
          groups[pipeTab].map((j) => <JobCard key={j.id} job={j} onAction={onAction} busy={busyId === j.id} onPreview={setPreviewJob} onChat={setChatJob} isOwner={isOwner}
            brandReady={brand.has_logo} onApplyBrand={applyBrand} forms={leadForms} onSetForm={setJobForm} onSetAdset={setJobAdset} onUpdateCard={updateCard}
            canArchive={canArchive} onArchive={archiveJob} />)
        )}
      </div>

      {previewJob && <AdPreview job={previewJob} pageName={pub.page_name || "Training Basket"} onClose={() => setPreviewJob(null)} />}
      {chatJob && <TeamChat job={chatJob} onClose={() => setChatJob(null)} onChanged={loadJobs} />}
    </div>
  );
}
